import type { FC } from 'react';
import loadable from '@loadable/component';
import type { ScoreProps } from '@components/Score';
import KeyMetric from '@components/KeyMetric';
import {
  SnowflakeScore,
  SectionTitle,
  Description,
  ScoreLabel,
} from './styled';
import DeferRenderUntilIntersecting from '@components/DeferRenderUntilIntersecting';
import { useCompanySplitDetect } from '@/hooks/useCompanySplitDetect';
import { SplitSectionTitle } from '@/pages/CompanySplit/components/SplitSectionTitle/SplitSectionTitle';
import type { CompanyReportSection } from '@/pages/CompanySplit/useSection';
const Score = loadable(() => import('@components/Score'), { ssr: false });

export interface ReportSectionHeaderProps {
  title: string;
  score?: number;
  keyMetric?: string | null;
  keyMetricTitle?: string | null;
  sectionDescription?: string;
  scoreValues?: ScoreProps['values'];
  skipDefer?: boolean;
  section?: CompanyReportSection;
}

const defaultProps = {
  title: '',
  sectionDescription: '',
};

const ReportSectionHeader: FC<ReportSectionHeaderProps> = ({
  title,
  score,
  keyMetric,
  keyMetricTitle,
  sectionDescription,
  scoreValues,
  skipDefer,
  children,
  section,
}) => {
  const isCompanySplit = useCompanySplitDetect();

  return (
    <>
      {section && isCompanySplit ? (
        //Section titles for company report split - condition will be removed after experiment
        <SplitSectionTitle title={title} section={section} />
      ) : (
        <SectionTitle>{title}</SectionTitle>
      )}

      {sectionDescription && (
        <>
          <Description>{sectionDescription}</Description>
          {score !== undefined && score > -1 && (
            <DeferRenderUntilIntersecting skipDefer={skipDefer}>
              {() => (
                <SnowflakeScore>
                  <h4>
                    Analysis Checks <ScoreLabel>{score}/6</ScoreLabel>
                  </h4>
                  <Score values={scoreValues} />
                </SnowflakeScore>
              )}
            </DeferRenderUntilIntersecting>
          )}
          {keyMetric && keyMetric.length > 0 && (
            <>
              <KeyMetric
                title={keyMetricTitle || `Key Metric`}
                size="largest"
                metric={keyMetric}
              />
            </>
          )}
        </>
      )}
      {children}
      <hr />
    </>
  );
};

ReportSectionHeader.defaultProps = defaultProps;

export default ReportSectionHeader;
